import moment from 'moment';
import { ILeaderboardConfig, LeaderboardFrequency, LeaderboardMetric } from './leaderboard-config';

export interface ILeaderboardInstance {
  metric: LeaderboardMetric;
  leaderboardId: string;
  startDate: string;
  endDate: string;
  team?: string;
  teamSize?: number;
  calculatedScore?: number;
}

export function getLeaderboardInstance(
  leaderboardConfig: ILeaderboardConfig,
  date: Date,
  activeLeaderboards: boolean = true,
  ): ILeaderboardInstance {
  const leaderboardId = leaderboardConfig._id;
  let startDate;
  let endDate;

  if (leaderboardConfig.frequency !== 'one-time') {
    const dateForCalculatingFrequencyDateRange = setCalculationDate(leaderboardConfig, date, activeLeaderboards);

    const dateRange = activeLeaderboards
      ? calculateFrequencyDateRange(
        leaderboardConfig.frequency,
        dateForCalculatingFrequencyDateRange,
        leaderboardConfig.timezone,
      )
      : calculateInactiveFrequencyDateRange(
        leaderboardConfig.frequency,
        dateForCalculatingFrequencyDateRange,
        leaderboardConfig.timezone,
      );

    startDate = dateRange.startDate.toISOString();
    endDate = dateRange.endDate.toISOString();
  } else {
    startDate = leaderboardConfig.startDate.toISOString();

    if (!leaderboardConfig.endDate) {
      throw new Error('endDate field is required for non recurring leaderboards');
    }
    endDate = leaderboardConfig.endDate.toISOString();
  }

  return {
    metric: leaderboardConfig.metric,
    leaderboardId,
    startDate,
    endDate,
  };
}

function setCalculationDate(
  leaderboardConfig: ILeaderboardConfig,
  date: Date,
  activeLeaderboard: boolean,
  ): Date {
  if (leaderboardConfig.startDate > date) {
    return leaderboardConfig.startDate;
  }
  if (!activeLeaderboard && leaderboardConfig.endDate && leaderboardConfig.endDate < date) {
    return leaderboardConfig.endDate;
  }
  return date;
}

function calculateFrequencyDateRange(frequency: LeaderboardFrequency, date: Date, timezone: string):
  {startDate: Date, endDate: Date} {
  const momentWithTimezone = moment(date).tz(timezone);
  switch (frequency) {
    case 'daily':
      return {
        startDate: momentWithTimezone.startOf('day').toDate(),
        endDate: momentWithTimezone.endOf('day').toDate(),
      };
    case 'weekly':
      return {
        startDate: momentWithTimezone.startOf('isoWeek').toDate(),
        endDate: momentWithTimezone.endOf('isoWeek').toDate(),
      };
    case 'monthly':
      return {
        startDate: momentWithTimezone.startOf('month').toDate(),
        endDate: momentWithTimezone.endOf('month').toDate(),
      };
    case 'one-time':
      throw new Error('one-time leaderboard does not have a frequency date range');
  }
}

function calculateInactiveFrequencyDateRange(frequency: LeaderboardFrequency, date: Date, timezone: string):
  {startDate: Date, endDate: Date} {
  const momentWithTimezone = moment(date).tz(timezone);
  switch (frequency) {
    case 'daily':
      const startOfToday = momentWithTimezone.startOf('day').toDate();
      const endOfToday = momentWithTimezone.endOf('day').toDate();
      const startOfDay = new Date(startOfToday.getTime() - (24 * 60 * 60 * 1000));
      const endOfDay = new Date(endOfToday.getTime() - (24 * 60 * 60 * 1000));
      return {
        startDate: startOfDay,
        endDate: endOfDay,
      };

    case 'weekly':
      const startOfThisWeek = momentWithTimezone.startOf('isoWeek').toDate();
      const endOfThisWeek = momentWithTimezone.endOf('isoWeek').toDate();
      const startOfWeek = new Date(startOfThisWeek.getTime() - (7 * 24 * 60 * 60 * 1000));
      const endOfWeek = new Date(endOfThisWeek.getTime() - (7 * 24 * 60 * 60 * 1000));
      return {
        startDate: startOfWeek,
        endDate: endOfWeek,
      };
    case 'monthly':
      const startOfThisMonth = momentWithTimezone.startOf('month').toDate();
      const endOfMonth = new Date(startOfThisMonth.getTime() - 1); // Gets the instant of the last month
      const momentInMonth = moment(endOfMonth).tz(timezone);
      const startOfMonth = momentInMonth.startOf('month').toDate();
      return {
        startDate: startOfMonth,
        endDate: endOfMonth,
      };
    case 'one-time':
      throw new Error('one-time leaderboard does not have a frequency date range');
  }
}
